import { useEffect, useRef, useState, useCallback } from 'react';
import {
  PencilIcon,
  TrashIcon,
  AnnotationIcon,
  CursorClickIcon,
  PhotographIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';

import useLocale from 'next-translate/useTranslation';
import Spinner from '../Spinner';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface ImageAnnotatorProps {
  imageUrl: string;
  onSave?: (annotatedImageUrl: string, onComplete: () => void) => void;
  onCancel?: () => void;
  className?: string;
}

type DrawingTool =
  | 'pen'
  | 'rectangle'
  | 'circle'
  | 'arrow'
  | 'text'
  | 'select'
  | 'image';

type DrawingElement = {
  id: string;
  type: 'path' | 'rectangle' | 'circle' | 'arrow' | 'text' | 'image';
  points?: { x: number; y: number }[];
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  width?: number;
  height?: number;
  radius?: number;
  color: string;
  strokeWidth: number;
  text?: string;
  fontSize?: number;
  image?: HTMLImageElement;
  originalWidth?: number;
  originalHeight?: number;
  rotation?: number; // Rotation in degrees
};

type ResizeHandle =
  | 'nw'
  | 'ne'
  | 'sw'
  | 'se'
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'rotate'
  | null;

const DEFAULT_COLOR = '#EF4444'; // red-500
const DEFAULT_STROKE_WIDTH = 7;
const DEFAULT_FONT_SIZE = 32;

export default function ImageAnnotator({
  imageUrl,
  onSave,
  onCancel,
  className = '',
}: ImageAnnotatorProps) {
  const { t } = useLocale('ai-interior');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const logicalSizeRef = useRef({ width: 0, height: 0 });
  const tempElementRef = useRef<DrawingElement | null>(null);
  const previewShapeRef = useRef<{
    tool: 'rectangle' | 'circle' | 'arrow';
    start: { x: number; y: number };
    end: { x: number; y: number };
  } | null>(null);
  const currentPathRef = useRef<{ x: number; y: number }[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState<DrawingTool>('pen');
  const currentColor = DEFAULT_COLOR;
  const strokeWidth = DEFAULT_STROKE_WIDTH;
  const fontSize = DEFAULT_FONT_SIZE;
  const [elements, setElements] = useState<DrawingElement[]>([]);
  const [history, setHistory] = useState<DrawingElement[][]>([[]]);
  const [historyStep, setHistoryStep] = useState(0);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputPosition, setTextInputPosition] = useState({ x: 0, y: 0 });
  const [textInput, setTextInput] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [activeResizeHandle, setActiveResizeHandle] =
    useState<ResizeHandle>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [rotationStartAngle, setRotationStartAngle] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Handle furniture/object image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Scale image to fit nicely on canvas (max 25% of canvas width)
        const maxWidth = (canvas.width / window.devicePixelRatio) * 0.25;
        const scale = img.width > maxWidth ? maxWidth / img.width : 1;
        const width = img.width * scale;
        const height = img.height * scale;

        // Center the image on canvas
        const startX = (canvas.width / window.devicePixelRatio - width) / 2;
        const startY = (canvas.height / window.devicePixelRatio - height) / 2;

        const newElement: DrawingElement = {
          id: Date.now().toString(),
          type: 'image',
          startX,
          startY,
          width,
          height,
          originalWidth: img.width,
          originalHeight: img.height,
          image: img,
          color: 'transparent',
          strokeWidth: 0,
          rotation: 0,
        };

        const newElements = [...elements, newElement];
        setElements(newElements);

        // Add to history
        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(newElements);
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);

        // Switch to select tool to immediately allow moving/resizing
        setCurrentTool('select');
        setSelectedElementId(newElement.id);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);

    // Reset input
    e.target.value = '';
  };

  // Get resize handle at position
  const getResizeHandle = (
    pos: { x: number; y: number },
    element: DrawingElement,
  ): ResizeHandle => {
    if (element.type !== 'image' && element.type !== 'rectangle') return null;
    if (
      element.startX === undefined ||
      element.startY === undefined ||
      element.width === undefined ||
      element.height === undefined
    )
      return null;

    const handleSize = 12;
    const x = element.startX;
    const y = element.startY;
    const w = element.width;
    const h = element.height;
    const rotation = element.rotation || 0;

    // Get center of the element
    const centerX = x + w / 2;
    const centerY = y + h / 2;

    // Transform click position to element's local coordinate system (unrotated)
    const rad = (-rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = pos.x - centerX;
    const dy = pos.y - centerY;
    const localX = centerX + dx * cos - dy * sin;
    const localY = centerY + dx * sin + dy * cos;

    // Check rotation handle (above the element)
    const rotateHandleY = y - 30;
    const rotateHandleDist = Math.sqrt(
      Math.pow(localX - (x + w / 2), 2) + Math.pow(localY - rotateHandleY, 2),
    );
    if (rotateHandleDist < handleSize) return 'rotate';

    // Corner handles
    if (Math.abs(localX - x) < handleSize && Math.abs(localY - y) < handleSize)
      return 'nw';
    if (
      Math.abs(localX - (x + w)) < handleSize &&
      Math.abs(localY - y) < handleSize
    )
      return 'ne';
    if (
      Math.abs(localX - x) < handleSize &&
      Math.abs(localY - (y + h)) < handleSize
    )
      return 'sw';
    if (
      Math.abs(localX - (x + w)) < handleSize &&
      Math.abs(localY - (y + h)) < handleSize
    )
      return 'se';

    // Edge handles
    if (
      Math.abs(localX - (x + w / 2)) < handleSize &&
      Math.abs(localY - y) < handleSize
    )
      return 'n';
    if (
      Math.abs(localX - (x + w / 2)) < handleSize &&
      Math.abs(localY - (y + h)) < handleSize
    )
      return 's';
    if (
      Math.abs(localX - x) < handleSize &&
      Math.abs(localY - (y + h / 2)) < handleSize
    )
      return 'w';
    if (
      Math.abs(localX - (x + w)) < handleSize &&
      Math.abs(localY - (y + h / 2)) < handleSize
    )
      return 'e';

    return null;
  };

  // Get cursor style for resize handle
  const getCursorForHandle = (handle: ResizeHandle): string => {
    switch (handle) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      case 'rotate':
        return 'grab';
      default:
        return 'default';
    }
  };

  // Redraw canvas with image and all elements
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const img = imageRef.current;

    if (!canvas || !ctx || !img) return;

    const dpr = window.devicePixelRatio || 1;

    // Reset transform to clear in physical pixels
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Scale to logical pixels
    ctx.scale(dpr, dpr);

    // Draw image scaled to fit canvas (using logical dimensions)
    const logicalWidth = canvas.width / dpr;
    const logicalHeight = canvas.height / dpr;
    ctx.drawImage(img, 0, 0, logicalWidth, logicalHeight);

    // Draw all elements
    elements.forEach((element) => {
      // Skip element if it's being manipulated (will be drawn from tempElementRef)
      if (tempElementRef.current && element.id === tempElementRef.current.id) {
        return;
      }

      ctx.strokeStyle = element.color;
      ctx.lineWidth = element.strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      switch (element.type) {
        case 'path':
          if (element.points && element.points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(element.points[0].x, element.points[0].y);
            element.points.forEach((point) => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;

        case 'rectangle':
          if (
            element.startX !== undefined &&
            element.startY !== undefined &&
            element.width !== undefined &&
            element.height !== undefined
          ) {
            ctx.strokeRect(
              element.startX,
              element.startY,
              element.width,
              element.height,
            );
          }
          break;

        case 'circle':
          if (
            element.startX !== undefined &&
            element.startY !== undefined &&
            element.radius !== undefined
          ) {
            ctx.beginPath();
            ctx.arc(
              element.startX,
              element.startY,
              element.radius,
              0,
              Math.PI * 2,
            );
            ctx.stroke();
          }
          break;

        case 'arrow':
          if (
            element.startX !== undefined &&
            element.startY !== undefined &&
            element.endX !== undefined &&
            element.endY !== undefined
          ) {
            // Draw line
            ctx.beginPath();
            ctx.moveTo(element.startX, element.startY);
            ctx.lineTo(element.endX, element.endY);
            ctx.stroke();

            // Draw arrowhead
            const angle = Math.atan2(
              element.endY - element.startY,
              element.endX - element.startX,
            );
            const arrowLength = 15;
            ctx.beginPath();
            ctx.moveTo(element.endX, element.endY);
            ctx.lineTo(
              element.endX - arrowLength * Math.cos(angle - Math.PI / 6),
              element.endY - arrowLength * Math.sin(angle - Math.PI / 6),
            );
            ctx.moveTo(element.endX, element.endY);
            ctx.lineTo(
              element.endX - arrowLength * Math.cos(angle + Math.PI / 6),
              element.endY - arrowLength * Math.sin(angle + Math.PI / 6),
            );
            ctx.stroke();
          }
          break;

        case 'text':
          if (
            element.text &&
            element.startX !== undefined &&
            element.startY !== undefined
          ) {
            ctx.fillStyle = element.color;
            ctx.font = `${element.fontSize || 20}px Arial`;
            ctx.fillText(element.text, element.startX, element.startY);
          }
          break;

        case 'image':
          if (
            element.image &&
            element.startX !== undefined &&
            element.startY !== undefined &&
            element.width !== undefined &&
            element.height !== undefined
          ) {
            const centerX = element.startX + element.width / 2;
            const centerY = element.startY + element.height / 2;
            const rotation = element.rotation || 0;

            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.drawImage(
              element.image,
              -element.width / 2,
              -element.height / 2,
              element.width,
              element.height,
            );
            ctx.restore();
          }
          break;
      }
    });

    // Draw current path being drawn
    if (
      currentPathRef.current.length > 0 &&
      currentTool === 'pen' &&
      isDrawing
    ) {
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(currentPathRef.current[0].x, currentPathRef.current[0].y);
      currentPathRef.current.forEach((point) => {
        ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    }

    // Draw preview shapes (rectangle, circle, arrow)
    if (previewShapeRef.current && isDrawing) {
      const preview = previewShapeRef.current;
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (preview.tool === 'rectangle') {
        const width = preview.end.x - preview.start.x;
        const height = preview.end.y - preview.start.y;
        ctx.strokeRect(preview.start.x, preview.start.y, width, height);
      } else if (preview.tool === 'circle') {
        const radius = Math.sqrt(
          Math.pow(preview.end.x - preview.start.x, 2) +
            Math.pow(preview.end.y - preview.start.y, 2),
        );
        ctx.beginPath();
        ctx.arc(preview.start.x, preview.start.y, radius, 0, Math.PI * 2);
        ctx.stroke();
      } else if (preview.tool === 'arrow') {
        ctx.beginPath();
        ctx.moveTo(preview.start.x, preview.start.y);
        ctx.lineTo(preview.end.x, preview.end.y);
        ctx.stroke();

        // Draw arrowhead
        const angle = Math.atan2(
          preview.end.y - preview.start.y,
          preview.end.x - preview.start.x,
        );
        const arrowLength = 15;
        ctx.beginPath();
        ctx.moveTo(preview.end.x, preview.end.y);
        ctx.lineTo(
          preview.end.x - arrowLength * Math.cos(angle - Math.PI / 6),
          preview.end.y - arrowLength * Math.sin(angle - Math.PI / 6),
        );
        ctx.moveTo(preview.end.x, preview.end.y);
        ctx.lineTo(
          preview.end.x - arrowLength * Math.cos(angle + Math.PI / 6),
          preview.end.y - arrowLength * Math.sin(angle + Math.PI / 6),
        );
        ctx.stroke();
      }
    }

    // Draw temporary element being manipulated (drag/resize/rotate)
    if (tempElementRef.current) {
      const tempEl = tempElementRef.current;
      ctx.strokeStyle = tempEl.color;
      ctx.lineWidth = tempEl.strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      switch (tempEl.type) {
        case 'path':
          if (tempEl.points && tempEl.points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(tempEl.points[0].x, tempEl.points[0].y);
            tempEl.points.forEach((point) => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;

        case 'rectangle':
          if (
            tempEl.startX !== undefined &&
            tempEl.startY !== undefined &&
            tempEl.width !== undefined &&
            tempEl.height !== undefined
          ) {
            ctx.strokeRect(
              tempEl.startX,
              tempEl.startY,
              tempEl.width,
              tempEl.height,
            );
          }
          break;

        case 'circle':
          if (
            tempEl.startX !== undefined &&
            tempEl.startY !== undefined &&
            tempEl.radius !== undefined
          ) {
            ctx.beginPath();
            ctx.arc(
              tempEl.startX,
              tempEl.startY,
              tempEl.radius,
              0,
              Math.PI * 2,
            );
            ctx.stroke();
          }
          break;

        case 'arrow':
          if (
            tempEl.startX !== undefined &&
            tempEl.startY !== undefined &&
            tempEl.endX !== undefined &&
            tempEl.endY !== undefined
          ) {
            // Draw line
            ctx.beginPath();
            ctx.moveTo(tempEl.startX, tempEl.startY);
            ctx.lineTo(tempEl.endX, tempEl.endY);
            ctx.stroke();

            // Draw arrowhead
            const angle = Math.atan2(
              tempEl.endY - tempEl.startY,
              tempEl.endX - tempEl.startX,
            );
            const arrowLength = 15;
            ctx.beginPath();
            ctx.moveTo(tempEl.endX, tempEl.endY);
            ctx.lineTo(
              tempEl.endX - arrowLength * Math.cos(angle - Math.PI / 6),
              tempEl.endY - arrowLength * Math.sin(angle - Math.PI / 6),
            );
            ctx.moveTo(tempEl.endX, tempEl.endY);
            ctx.lineTo(
              tempEl.endX - arrowLength * Math.cos(angle + Math.PI / 6),
              tempEl.endY - arrowLength * Math.sin(angle + Math.PI / 6),
            );
            ctx.stroke();
          }
          break;

        case 'text':
          if (
            tempEl.text &&
            tempEl.startX !== undefined &&
            tempEl.startY !== undefined
          ) {
            ctx.fillStyle = tempEl.color;
            ctx.font = `${tempEl.fontSize || 20}px Arial`;
            ctx.fillText(tempEl.text, tempEl.startX, tempEl.startY);
          }
          break;

        case 'image':
          if (
            tempEl.image &&
            tempEl.startX !== undefined &&
            tempEl.startY !== undefined &&
            tempEl.width !== undefined &&
            tempEl.height !== undefined
          ) {
            const centerX = tempEl.startX + tempEl.width / 2;
            const centerY = tempEl.startY + tempEl.height / 2;
            const rotation = tempEl.rotation || 0;

            ctx.save();
            ctx.translate(centerX, centerY);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.drawImage(
              tempEl.image,
              -tempEl.width / 2,
              -tempEl.height / 2,
              tempEl.width,
              tempEl.height,
            );
            ctx.restore();
          }
          break;
      }
    }

    // Draw selection indicator for selected element
    if (selectedElementId) {
      // Use tempElementRef if it's being manipulated, otherwise find in elements
      const selectedElement =
        tempElementRef.current &&
        tempElementRef.current.id === selectedElementId
          ? tempElementRef.current
          : elements.find((el) => el.id === selectedElementId);
      if (selectedElement) {
        ctx.strokeStyle = '#2563EB'; // Blue-600
        ctx.lineWidth = 1.5;
        ctx.setLineDash([]); // Solid line for selection

        // Draw bounding box based on element type
        if (
          (selectedElement.type === 'rectangle' ||
            selectedElement.type === 'image') &&
          selectedElement.startX !== undefined &&
          selectedElement.startY !== undefined &&
          selectedElement.width !== undefined &&
          selectedElement.height !== undefined
        ) {
          const x = selectedElement.startX;
          const y = selectedElement.startY;
          const w = selectedElement.width;
          const h = selectedElement.height;
          const rotation = selectedElement.rotation || 0;

          const centerX = x + w / 2;
          const centerY = y + h / 2;

          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate((rotation * Math.PI) / 180);

          // Draw selection border
          ctx.strokeRect(-w / 2, -h / 2, w, h);

          // Draw resize handles for images
          if (selectedElement.type === 'image') {
            ctx.fillStyle = '#FFFFFF';
            ctx.strokeStyle = '#2563EB';
            ctx.lineWidth = 1.5;

            const handleSize = 10;
            const halfHandle = handleSize / 2;

            // Draw handle function
            const drawHandle = (hx: number, hy: number) => {
              ctx.beginPath();
              ctx.rect(
                hx - halfHandle,
                hy - halfHandle,
                handleSize,
                handleSize,
              );
              ctx.fill();
              ctx.stroke();
            };

            // Corner handles
            drawHandle(-w / 2, -h / 2); // NW
            drawHandle(w / 2, -h / 2); // NE
            drawHandle(-w / 2, h / 2); // SW
            drawHandle(w / 2, h / 2); // SE

            // Rotation handle (above the element)
            const rotHandleY = -h / 2 - 25;

            // Connection line
            ctx.beginPath();
            ctx.moveTo(0, -h / 2);
            ctx.lineTo(0, rotHandleY + 5); // Stop before circle
            ctx.stroke();

            // Handle circle
            ctx.beginPath();
            ctx.arc(0, rotHandleY, 5, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }

          ctx.restore();
        } else if (
          selectedElement.type === 'circle' &&
          selectedElement.startX !== undefined &&
          selectedElement.startY !== undefined &&
          selectedElement.radius !== undefined
        ) {
          ctx.beginPath();
          ctx.arc(
            selectedElement.startX,
            selectedElement.startY,
            selectedElement.radius + 5,
            0,
            Math.PI * 2,
          );
          ctx.stroke();
        } else if (
          selectedElement.type === 'text' &&
          selectedElement.startX !== undefined &&
          selectedElement.startY !== undefined &&
          selectedElement.text
        ) {
          ctx.font = `${selectedElement.fontSize || 20}px Arial`;
          const metrics = ctx.measureText(selectedElement.text);
          ctx.strokeRect(
            selectedElement.startX - 5,
            selectedElement.startY - (selectedElement.fontSize || 20) - 5,
            metrics.width + 10,
            (selectedElement.fontSize || 20) + 10,
          );
        } else if (
          selectedElement.type === 'arrow' &&
          selectedElement.startX !== undefined &&
          selectedElement.startY !== undefined &&
          selectedElement.endX !== undefined &&
          selectedElement.endY !== undefined
        ) {
          const minX = Math.min(selectedElement.startX, selectedElement.endX);
          const minY = Math.min(selectedElement.startY, selectedElement.endY);
          const maxX = Math.max(selectedElement.startX, selectedElement.endX);
          const maxY = Math.max(selectedElement.startY, selectedElement.endY);
          ctx.strokeRect(
            minX - 5,
            minY - 5,
            maxX - minX + 10,
            maxY - minY + 10,
          );
        } else if (selectedElement.type === 'path' && selectedElement.points) {
          const minX = Math.min(...selectedElement.points.map((p) => p.x));
          const minY = Math.min(...selectedElement.points.map((p) => p.y));
          const maxX = Math.max(...selectedElement.points.map((p) => p.x));
          const maxY = Math.max(...selectedElement.points.map((p) => p.y));
          ctx.strokeRect(
            minX - 5,
            minY - 5,
            maxX - minX + 10,
            maxY - minY + 10,
          );
        }
      }
    }
  }, [elements, currentColor, strokeWidth, currentTool, selectedElementId]);

  // Load and draw image
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const img = new Image();

    // Set crossOrigin to handle CORS
    img.crossOrigin = 'anonymous';

    const setupCanvas = (image: HTMLImageElement) => {
      if (canvas && ctx) {
        const maxWidth = 1200;
        const maxHeight = 800;
        let width = image.width;
        let height = image.height;

        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (maxHeight / height) * width;
          height = maxHeight;
        }

        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        logicalSizeRef.current = { width, height };

        // Store image reference
        (imageRef as any).current = image;
        setImageLoaded(true);

        // Initial draw
        redrawCanvas();
      }
    };

    img.onload = () => setupCanvas(img);

    img.onerror = () => {
      console.error('Failed to load image for annotation');
      alert(
        'Unable to load image for annotation. The image may have CORS restrictions.',
      );
    };

    // Add cache-busting parameter to avoid using cached non-CORS version
    // Browsers may cache images without CORS headers from <img> tags elsewhere
    const separator = imageUrl.includes('?') ? '&' : '?';
    img.src = `${imageUrl}${separator}_cors=${Date.now()}`;
  }, [imageUrl, redrawCanvas]);

  // Redraw when elements change
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  const getMousePosition = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const logicalW = logicalSizeRef.current.width || canvas.width;
    const logicalH = logicalSizeRef.current.height || canvas.height;

    const scaleX = logicalW / rect.width;
    const scaleY = logicalH / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const getTouchPosition = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const logicalW = logicalSizeRef.current.width || canvas.width;
    const logicalH = logicalSizeRef.current.height || canvas.height;

    const scaleX = logicalW / rect.width;
    const scaleY = logicalH / rect.height;

    const touch = e.touches.length > 0 ? e.touches[0] : e.changedTouches[0];

    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY,
    };
  };

  // Hit detection for elements
  const hitTestElement = (
    pos: { x: number; y: number },
    element: DrawingElement,
  ): boolean => {
    const threshold = 10; // Pixels of tolerance for hit detection

    switch (element.type) {
      case 'rectangle':
      case 'image':
        if (
          element.startX !== undefined &&
          element.startY !== undefined &&
          element.width !== undefined &&
          element.height !== undefined
        ) {
          const rotation = element.rotation || 0;
          const centerX = element.startX + element.width / 2;
          const centerY = element.startY + element.height / 2;

          // Transform click position to element's local coordinate system
          const rad = (-rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);
          const dx = pos.x - centerX;
          const dy = pos.y - centerY;
          const localX = centerX + dx * cos - dy * sin;
          const localY = centerY + dx * sin + dy * cos;

          return (
            localX >= element.startX - threshold &&
            localX <= element.startX + element.width + threshold &&
            localY >= element.startY - threshold &&
            localY <= element.startY + element.height + threshold
          );
        }
        break;

      case 'circle':
        if (
          element.startX !== undefined &&
          element.startY !== undefined &&
          element.radius !== undefined
        ) {
          const dist = Math.sqrt(
            Math.pow(pos.x - element.startX, 2) +
              Math.pow(pos.y - element.startY, 2),
          );
          return dist <= element.radius + threshold;
        }
        break;

      case 'arrow':
        if (
          element.startX !== undefined &&
          element.startY !== undefined &&
          element.endX !== undefined &&
          element.endY !== undefined
        ) {
          // Check if point is near the line
          const distToLine = pointToLineDistance(
            pos,
            { x: element.startX, y: element.startY },
            { x: element.endX, y: element.endY },
          );
          return distToLine <= threshold;
        }
        break;

      case 'text':
        if (
          element.startX !== undefined &&
          element.startY !== undefined &&
          element.text
        ) {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            ctx.font = `${element.fontSize || 20}px Arial`;
            const metrics = ctx.measureText(element.text);
            return (
              pos.x >= element.startX - threshold &&
              pos.x <= element.startX + metrics.width + threshold &&
              pos.y >= element.startY - (element.fontSize || 20) - threshold &&
              pos.y <= element.startY + threshold
            );
          }
        }
        break;

      case 'path':
        if (element.points && element.points.length > 0) {
          // Check if click is near any point in the path
          return element.points.some(
            (point) =>
              Math.abs(point.x - pos.x) <= threshold &&
              Math.abs(point.y - pos.y) <= threshold,
          );
        }
        break;
    }

    return false;
  };

  // Calculate distance from point to line segment
  const pointToLineDistance = (
    point: { x: number; y: number },
    lineStart: { x: number; y: number },
    lineEnd: { x: number; y: number },
  ): number => {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = lineStart.x;
      yy = lineStart.y;
    } else if (param > 1) {
      xx = lineEnd.x;
      yy = lineEnd.y;
    } else {
      xx = lineStart.x + param * C;
      yy = lineStart.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;

    return Math.sqrt(dx * dx + dy * dy);
  };

  const processStart = (pos: { x: number; y: number }) => {
    if (!imageLoaded) return;

    if (currentTool === 'select') {
      // Check if clicking on resize/rotate handle of selected element
      if (selectedElementId) {
        const selectedElement = elements.find(
          (el) => el.id === selectedElementId,
        );
        if (selectedElement && selectedElement.type === 'image') {
          const handle = getResizeHandle(pos, selectedElement);
          if (handle === 'rotate') {
            setIsRotating(true);
            // Calculate initial angle from center to mouse
            const centerX =
              (selectedElement.startX || 0) + (selectedElement.width || 0) / 2;
            const centerY =
              (selectedElement.startY || 0) + (selectedElement.height || 0) / 2;
            const angle =
              (Math.atan2(pos.y - centerY, pos.x - centerX) * 180) / Math.PI;
            setRotationStartAngle(angle - (selectedElement.rotation || 0));
            return;
          }
          if (handle) {
            setIsResizing(true);
            setActiveResizeHandle(handle);
            setDragOffset({ x: pos.x, y: pos.y });
            return;
          }
        }
      }

      // Find which element was clicked (check in reverse order for top-most element)
      const clickedElement = [...elements]
        .reverse()
        .find((el) => hitTestElement(pos, el));

      if (clickedElement) {
        setSelectedElementId(clickedElement.id);
        setIsDragging(true);

        // Calculate offset from click position to element position
        if (
          clickedElement.type === 'rectangle' ||
          clickedElement.type === 'circle' ||
          clickedElement.type === 'text' ||
          clickedElement.type === 'image'
        ) {
          setDragOffset({
            x: pos.x - (clickedElement.startX || 0),
            y: pos.y - (clickedElement.startY || 0),
          });
        } else if (clickedElement.type === 'arrow') {
          // For arrows, use the start point as reference
          setDragOffset({
            x: pos.x - (clickedElement.startX || 0),
            y: pos.y - (clickedElement.startY || 0),
          });
        } else if (clickedElement.type === 'path' && clickedElement.points) {
          // For paths, use the first point as reference
          setDragOffset({
            x: pos.x - clickedElement.points[0].x,
            y: pos.y - clickedElement.points[0].y,
          });
        }
      } else {
        // Clicked on empty space - deselect
        setSelectedElementId(null);
      }
    } else {
      // Drawing tools
      setIsDrawing(true);
      setSelectedElementId(null); // Deselect when using drawing tools

      switch (currentTool) {
        case 'pen':
          currentPathRef.current = [pos];
          break;
        case 'rectangle':
        case 'circle':
        case 'arrow':
          setStartPoint(pos);
          break;
        case 'text':
          setTextInputPosition(pos);
          setShowTextInput(true);
          setTimeout(() => textInputRef.current?.focus(), 100);
          break;
      }
    }
  };

  const processMove = (pos: { x: number; y: number }) => {
    if (!imageLoaded) return;

    // Update cursor for resize handles
    if (
      currentTool === 'select' &&
      selectedElementId &&
      !isDragging &&
      !isResizing &&
      !isRotating
    ) {
      const selectedElement =
        tempElementRef.current &&
        tempElementRef.current.id === selectedElementId
          ? tempElementRef.current
          : elements.find((el) => el.id === selectedElementId);
      if (selectedElement && selectedElement.type === 'image') {
        const handle = getResizeHandle(pos, selectedElement);
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.style.cursor = handle
            ? getCursorForHandle(handle)
            : hitTestElement(pos, selectedElement)
              ? 'move'
              : 'default';
        }
      }
    }

    // Handle rotation
    if (currentTool === 'select' && isRotating && selectedElementId) {
      const element = elements.find((el) => el.id === selectedElementId);
      if (element && element.type === 'image') {
        const centerX = (element.startX || 0) + (element.width || 0) / 2;
        const centerY = (element.startY || 0) + (element.height || 0) / 2;

        // Calculate angle from center to current mouse position
        const angle =
          (Math.atan2(pos.y - centerY, pos.x - centerX) * 180) / Math.PI;
        let newRotation = angle - rotationStartAngle;

        // Normalize to 0-360
        while (newRotation < 0) newRotation += 360;
        while (newRotation >= 360) newRotation -= 360;

        tempElementRef.current = { ...element, rotation: newRotation };
        redrawCanvas();
      }
      return;
    }

    // Handle resizing
    if (
      currentTool === 'select' &&
      isResizing &&
      selectedElementId &&
      activeResizeHandle
    ) {
      const element = elements.find((el) => el.id === selectedElementId);
      if (
        element &&
        (element.type === 'image' || element.type === 'rectangle') &&
        element.startX !== undefined &&
        element.startY !== undefined &&
        element.width !== undefined &&
        element.height !== undefined
      ) {
        const updatedElement = { ...element };

        const aspectRatio =
          element.type === 'image'
            ? (element.originalWidth || element.width) /
              (element.originalHeight || element.height)
            : element.width / element.height;

        let newX = element.startX;
        let newY = element.startY;
        let newW = element.width;
        let newH = element.height;

        switch (activeResizeHandle) {
          case 'se':
            newW = Math.max(50, pos.x - element.startX);
            newH = newW / aspectRatio;
            break;
          case 'sw':
            newW = Math.max(50, element.startX + element.width - pos.x);
            newH = newW / aspectRatio;
            newX = element.startX + element.width - newW;
            break;
          case 'ne':
            newW = Math.max(50, pos.x - element.startX);
            newH = newW / aspectRatio;
            newY = element.startY + element.height - newH;
            break;
          case 'nw':
            newW = Math.max(50, element.startX + element.width - pos.x);
            newH = newW / aspectRatio;
            newX = element.startX + element.width - newW;
            newY = element.startY + element.height - newH;
            break;
          case 'e':
            newW = Math.max(50, pos.x - element.startX);
            newH = newW / aspectRatio;
            break;
          case 'w':
            newW = Math.max(50, element.startX + element.width - pos.x);
            newH = newW / aspectRatio;
            newX = element.startX + element.width - newW;
            break;
          case 'n':
            newH = Math.max(50, element.startY + element.height - pos.y);
            newW = newH * aspectRatio;
            newY = element.startY + element.height - newH;
            break;
          case 's':
            newH = Math.max(50, pos.y - element.startY);
            newW = newH * aspectRatio;
            break;
        }

        updatedElement.startX = newX;
        updatedElement.startY = newY;
        updatedElement.width = newW;
        updatedElement.height = newH;

        tempElementRef.current = updatedElement;
        redrawCanvas();
      }
      return;
    }

    // Handle dragging in select mode
    if (currentTool === 'select' && isDragging && selectedElementId) {
      const element = elements.find((el) => el.id === selectedElementId);
      if (element) {
        const updatedElement = { ...element };

        // Move the element based on its type
        if (
          element.type === 'rectangle' ||
          element.type === 'circle' ||
          element.type === 'text' ||
          element.type === 'image'
        ) {
          updatedElement.startX = pos.x - dragOffset.x;
          updatedElement.startY = pos.y - dragOffset.y;
        } else if (element.type === 'arrow') {
          const deltaX = pos.x - dragOffset.x - (element.startX || 0);
          const deltaY = pos.y - dragOffset.y - (element.startY || 0);
          updatedElement.startX = pos.x - dragOffset.x;
          updatedElement.startY = pos.y - dragOffset.y;
          if (element.endX !== undefined && element.endY !== undefined) {
            updatedElement.endX = element.endX + deltaX;
            updatedElement.endY = element.endY + deltaY;
          }
        } else if (element.type === 'path' && element.points) {
          const deltaX = pos.x - dragOffset.x - element.points[0].x;
          const deltaY = pos.y - dragOffset.y - element.points[0].y;
          updatedElement.points = element.points.map((point) => ({
            x: point.x + deltaX,
            y: point.y + deltaY,
          }));
        }

        tempElementRef.current = updatedElement;
        redrawCanvas();
      }
      return;
    }

    // Handle drawing
    if (!isDrawing) return;

    switch (currentTool) {
      case 'pen':
        currentPathRef.current = [...currentPathRef.current, pos];
        redrawCanvas();
        break;
      case 'rectangle':
      case 'circle':
      case 'arrow':
        if (startPoint) {
          previewShapeRef.current = {
            tool: currentTool,
            start: startPoint,
            end: pos,
          };
          redrawCanvas();
        }
        break;
    }
  };

  const processEnd = (pos: { x: number; y: number }) => {
    if (!imageLoaded) return;

    // Handle rotation end
    if (currentTool === 'select' && isRotating) {
      setIsRotating(false);

      // Commit tempElementRef to elements
      if (tempElementRef.current) {
        const newElements = elements.map((el) =>
          el.id === tempElementRef.current?.id ? tempElementRef.current : el,
        );
        setElements(newElements);

        // Add to history for undo/redo
        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(newElements);
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);

        tempElementRef.current = null;
      }
      return;
    }

    // Handle resize end
    if (currentTool === 'select' && isResizing) {
      setIsResizing(false);
      setActiveResizeHandle(null);

      // Commit tempElementRef to elements
      if (tempElementRef.current) {
        const newElements = elements.map((el) =>
          el.id === tempElementRef.current?.id ? tempElementRef.current : el,
        );
        setElements(newElements);

        // Add to history for undo/redo
        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(newElements);
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);

        tempElementRef.current = null;
      }
      return;
    }

    // Handle drag end in select mode
    if (currentTool === 'select' && isDragging) {
      setIsDragging(false);

      // Commit tempElementRef to elements
      if (tempElementRef.current) {
        const newElements = elements.map((el) =>
          el.id === tempElementRef.current?.id ? tempElementRef.current : el,
        );
        setElements(newElements);

        // Add to history for undo/redo
        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(newElements);
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);

        tempElementRef.current = null;
      }
      return;
    }

    if (!isDrawing) return;

    setIsDrawing(false);

    let newElement: DrawingElement | null = null;

    switch (currentTool) {
      case 'pen':
        if (currentPathRef.current.length > 0) {
          newElement = {
            id: Date.now().toString(),
            type: 'path',
            points: currentPathRef.current,
            color: currentColor,
            strokeWidth,
          };
        }
        break;
      case 'rectangle':
        if (startPoint) {
          const startX = Math.min(startPoint.x, pos.x);
          const startY = Math.min(startPoint.y, pos.y);
          const width = Math.abs(pos.x - startPoint.x);
          const height = Math.abs(pos.y - startPoint.y);

          newElement = {
            id: Date.now().toString(),
            type: 'rectangle',
            startX,
            startY,
            width,
            height,
            color: currentColor,
            strokeWidth,
          };
        }
        break;
      case 'circle':
        if (startPoint) {
          const radius = Math.sqrt(
            Math.pow(pos.x - startPoint.x, 2) +
              Math.pow(pos.y - startPoint.y, 2),
          );
          newElement = {
            id: Date.now().toString(),
            type: 'circle',
            startX: startPoint.x,
            startY: startPoint.y,
            radius,
            color: currentColor,
            strokeWidth,
          };
        }
        break;
      case 'arrow':
        if (startPoint) {
          newElement = {
            id: Date.now().toString(),
            type: 'arrow',
            startX: startPoint.x,
            startY: startPoint.y,
            endX: pos.x,
            endY: pos.y,
            color: currentColor,
            strokeWidth,
          };
        }
        break;
    }

    if (newElement) {
      const newElements = [...elements, newElement];
      setElements(newElements);

      // Add to history
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(newElements);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    }

    currentPathRef.current = [];
    previewShapeRef.current = null;
    setStartPoint(null);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePosition(e);
    processStart(pos);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePosition(e);
    processMove(pos);
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePosition(e);
    processEnd(pos);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getTouchPosition(e);
    processStart(pos);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getTouchPosition(e);
    processMove(pos);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const pos = getTouchPosition(e);
    processEnd(pos);
  };

  const handleTextSubmit = () => {
    if (textInput.trim() && textInputPosition) {
      const newElement: DrawingElement = {
        id: Date.now().toString(),
        type: 'text',
        startX: textInputPosition.x,
        startY: textInputPosition.y,
        text: textInput,
        color: currentColor,
        fontSize,
        strokeWidth: 1,
      };

      const newElements = [...elements, newElement];
      setElements(newElements);

      // Add to history
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(newElements);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    }

    setShowTextInput(false);
    setTextInput('');
  };

  const undo = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      setElements(history[newStep]);
      setSelectedElementId(null);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      setElements(history[newStep]);
      setSelectedElementId(null);
    }
  };

  const clearAll = () => {
    setElements([]);
    setSelectedElementId(null);
    const newHistory = [...history, []];
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const deleteSelected = () => {
    if (selectedElementId) {
      const newElements = elements.filter((el) => el.id !== selectedElementId);
      setElements(newElements);
      setSelectedElementId(null);

      // Add to history
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(newElements);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    }
  };

  // Rotate selected image by 90 degrees
  const rotateSelected90 = () => {
    if (selectedElementId) {
      const elementIndex = elements.findIndex(
        (el) => el.id === selectedElementId,
      );
      if (elementIndex !== -1) {
        const element = elements[elementIndex];
        if (element.type === 'image') {
          const newElements = [...elements];
          const currentRotation = element.rotation || 0;
          newElements[elementIndex] = {
            ...element,
            rotation: (currentRotation + 90) % 360,
          };
          setElements(newElements);

          // Add to history
          const newHistory = history.slice(0, historyStep + 1);
          newHistory.push(newElements);
          setHistory(newHistory);
          setHistoryStep(newHistory.length - 1);
        }
      }
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Input fields should capture their own events
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA' ||
        showTextInput
      ) {
        if (e.key === 'Escape') {
          setShowTextInput(false);
          setTextInput('');
        }
        return;
      }

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      // Undo/Redo
      if (isCmdOrCtrl && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      // Nudge controls with Arrow keys
      if (
        selectedElementId &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
      ) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const elementIndex = elements.findIndex(
          (el) => el.id === selectedElementId,
        );

        if (elementIndex !== -1) {
          const element = elements[elementIndex];
          const newElements = [...elements];
          const updatedElement = { ...element };

          if (
            updatedElement.startX !== undefined &&
            updatedElement.startY !== undefined
          ) {
            switch (e.key) {
              case 'ArrowUp':
                updatedElement.startY -= step;
                break;
              case 'ArrowDown':
                updatedElement.startY += step;
                break;
              case 'ArrowLeft':
                updatedElement.startX -= step;
                break;
              case 'ArrowRight':
                updatedElement.startX += step;
                break;
            }

            // Handle special cases for path/arrows if needed, but basic move works for startX/Y
            if (
              updatedElement.type === 'arrow' &&
              updatedElement.endX !== undefined &&
              updatedElement.endY !== undefined
            ) {
              switch (e.key) {
                case 'ArrowUp':
                  updatedElement.endY -= step;
                  break;
                case 'ArrowDown':
                  updatedElement.endY += step;
                  break;
                case 'ArrowLeft':
                  updatedElement.endX -= step;
                  break;
                case 'ArrowRight':
                  updatedElement.endX += step;
                  break;
              }
            } else if (element.type === 'path' && element.points) {
              updatedElement.points = element.points.map((p) => ({
                x:
                  p.x +
                  (e.key === 'ArrowLeft'
                    ? -step
                    : e.key === 'ArrowRight'
                      ? step
                      : 0),
                y:
                  p.y +
                  (e.key === 'ArrowUp'
                    ? -step
                    : e.key === 'ArrowDown'
                      ? step
                      : 0),
              }));
            }

            newElements[elementIndex] = updatedElement;
            setElements(newElements);
          }
        }
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElementId) {
          e.preventDefault();
          deleteSelected();
        }
      }
      // R key to rotate 90 degrees
      if (e.key === 'r' || e.key === 'R') {
        if (selectedElementId) {
          const element = elements.find((el) => el.id === selectedElementId);
          if (element?.type === 'image') {
            e.preventDefault();
            rotateSelected90();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedElementId,
    showTextInput,
    elements,
    history,
    historyStep,
    undo,
    redo,
  ]);

  const saveAnnotatedImage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || isSaving) return;

    setIsSaving(true);

    try {
      // Create a temporary canvas to export at logical size (not high DPI size)
      const tempCanvas = document.createElement('canvas');
      const dpr = window.devicePixelRatio || 1;

      // Use logical dimensions to ensure we save at the intended size
      const targetWidth = logicalSizeRef.current.width || canvas.width / dpr;
      const targetHeight = logicalSizeRef.current.height || canvas.height / dpr;

      tempCanvas.width = targetWidth;
      tempCanvas.height = targetHeight;

      const ctx = tempCanvas.getContext('2d');
      if (!ctx) throw new Error('Could not get temp canvas context');

      // Draw the high-res canvas content scaled down to the logical size
      ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);

      // Convert to blob
      tempCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          onSave?.(url, () => setIsSaving(false));
        } else {
          console.error('Failed to create blob from canvas');
          // Fallback: try to save as data URL
          try {
            const dataUrl = tempCanvas.toDataURL('image/png');
            onSave?.(dataUrl, () => setIsSaving(false));
          } catch (e) {
            console.error('Canvas is tainted, cannot export', e);
            alert(
              'Unable to save annotated image due to cross-origin restrictions. Please try uploading a local image.',
            );
            setIsSaving(false);
          }
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error saving annotated image:', error);
      alert('Unable to save annotated image. Please try again.');
      setIsSaving(false);
    }
  }, [onSave, isSaving]);

  // Check if selected element is an image
  const selectedElement = selectedElementId
    ? elements.find((el) => el.id === selectedElementId)
    : null;
  const isImageSelected = selectedElement?.type === 'image';

  return (
    <div
      className={`relative bg-white dark:bg-slate-800 rounded-lg shadow-lg flex flex-col ${className}`}
      style={{ height: '100%' }}
    >
      {/* Hidden file input for image upload */}
      <input
        ref={imageInputRef}
        type='file'
        accept='image/*'
        className='hidden'
        onChange={handleImageUpload}
      />

      {/* Toolbar */}
      <div className='absolute top-4 left-1/2 transform -translate-x-1/2 z-20 w-full px-2 sm:w-auto sm:px-0'>
        <div className='flex items-center justify-center gap-1 sm:gap-1.5 p-1 sm:p-1.5 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-2xl sm:rounded-full shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-200 flex-wrap sm:flex-nowrap'>
          {/* Drawing Tools Group */}
          <div className='flex items-center gap-0.5 sm:gap-1 px-0.5 sm:px-1'>
            {(
              [
                {
                  id: 'pen',
                  icon: PencilIcon,
                  label: 'Draw freehand',
                  showMobile: true,
                },
                {
                  id: 'rectangle',
                  icon: (props: React.ComponentProps<'svg'>) => (
                    <svg
                      {...props}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <rect
                        x='4'
                        y='6'
                        width='16'
                        height='12'
                        strokeWidth='2'
                        strokeLinecap='round'
                      />
                    </svg>
                  ),
                  label: 'Rectangle',
                  showMobile: false,
                },
                {
                  id: 'circle',
                  icon: (props: React.ComponentProps<'svg'>) => (
                    <svg
                      {...props}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <circle cx='12' cy='12' r='8' strokeWidth='2' />
                    </svg>
                  ),
                  label: 'Circle',
                  showMobile: false,
                },
                {
                  id: 'arrow',
                  icon: (props: React.ComponentProps<'svg'>) => (
                    <svg
                      {...props}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M14 5l7 7m0 0l-7 7m7-7H3'
                      />
                    </svg>
                  ),
                  label: 'Arrow',
                  showMobile: true,
                },
                {
                  id: 'text',
                  icon: AnnotationIcon,
                  label: 'Add text',
                  showMobile: false,
                },
              ] as const
            ).map((tool) => (
              <SimpleTooltip key={tool.id} title={t(tool.label)} side='bottom'>
                <button
                  onClick={() => setCurrentTool(tool.id)}
                  className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 ${
                    !tool.showMobile ? 'hidden sm:block' : ''
                  } ${
                    currentTool === tool.id
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 ring-2 ring-blue-500/20'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <tool.icon className='h-4 w-4 sm:h-5 sm:w-5' />
                </button>
              </SimpleTooltip>
            ))}
          </div>

          <div className='w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 hidden sm:block' />

          {/* Media Group - hidden on mobile */}
          <div className='hidden sm:flex items-center gap-1 px-1'>
            <SimpleTooltip title={t('Upload Image')} side='bottom'>
              <button
                onClick={() => imageInputRef.current?.click()}
                className='p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200'
              >
                <PhotographIcon className='h-5 w-5' />
              </button>
            </SimpleTooltip>
          </div>

          <div className='w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-700' />

          {/* Actions Group */}
          <div className='flex items-center gap-0.5 sm:gap-1 px-0.5 sm:px-1'>
            <SimpleTooltip title={t('Select & Move')} side='bottom'>
              <button
                onClick={() => setCurrentTool('select')}
                className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 ${
                  currentTool === 'select'
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 ring-2 ring-blue-500/20'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <CursorClickIcon className='h-4 w-4 sm:h-5 sm:w-5' />
              </button>
            </SimpleTooltip>

            {isImageSelected && (
              <SimpleTooltip title={t('Rotate 90°')} side='bottom'>
                <button
                  onClick={rotateSelected90}
                  className='p-1.5 sm:p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200'
                >
                  <RefreshIcon className='h-4 w-4 sm:h-5 sm:w-5' />
                </button>
              </SimpleTooltip>
            )}

            <div className='flex items-center gap-0.5'>
              <SimpleTooltip title={t('Undo')} side='bottom'>
                <button
                  onClick={undo}
                  disabled={historyStep === 0}
                  className='p-1.5 sm:p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200'
                >
                  <ChevronLeftIcon className='h-4 w-4 sm:h-5 sm:w-5' />
                </button>
              </SimpleTooltip>
              <SimpleTooltip title={t('Redo')} side='bottom'>
                <button
                  onClick={redo}
                  disabled={historyStep === history.length - 1}
                  className='p-1.5 sm:p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200'
                >
                  <ChevronRightIcon className='h-4 w-4 sm:h-5 sm:w-5' />
                </button>
              </SimpleTooltip>
            </div>

            <SimpleTooltip title={t('Clear All')} side='bottom'>
              <button
                onClick={clearAll}
                className='p-1.5 sm:p-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200'
              >
                <TrashIcon className='h-4 w-4 sm:h-5 sm:w-5' />
              </button>
            </SimpleTooltip>
          </div>

          <div className='w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-700' />

          {/* Save/Cancel Group */}
          <div className='flex items-center gap-1 sm:gap-2 pl-1 sm:pl-2 pr-0.5 sm:pr-1'>
            {onCancel && (
              <button
                onClick={onCancel}
                disabled={isSaving}
                className='px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50'
              >
                {t('Cancel')}
              </button>
            )}
            <button
              onClick={saveAnnotatedImage}
              disabled={isSaving}
              className='px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2'
            >
              {isSaving && (
                <svg
                  className='animate-spin h-3 w-3 sm:h-4 sm:w-4 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
              )}
              {isSaving ? t('Saving...') : t('Save')}
            </button>
          </div>
        </div>
      </div>

      {/* Canvas Container */}
      <div className='flex-1 pt-16 p-4 overflow-auto'>
        <div className='relative h-full flex items-center justify-center'>
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            className={`border border-gray-300 dark:border-gray-600 rounded ${
              currentTool === 'select'
                ? isDragging
                  ? 'cursor-move'
                  : isRotating
                    ? 'cursor-grabbing'
                    : 'cursor-pointer'
                : 'cursor-crosshair'
            }`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              touchAction: 'none',
            }}
          />

          {/* Text Input Overlay */}
          {showTextInput && (
            <input
              ref={textInputRef}
              type='text'
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTextSubmit();
                } else if (e.key === 'Escape') {
                  setShowTextInput(false);
                  setTextInput('');
                }
              }}
              onBlur={handleTextSubmit}
              className='absolute z-20 px-2 py-1 text-sm border border-gray-300 rounded bg-white dark:bg-gray-800 dark:border-gray-600'
              style={{
                left: `${textInputPosition.x}px`,
                top: `${textInputPosition.y - 10}px`,
                color: currentColor,
                fontSize: `${fontSize}px`,
              }}
              placeholder={t('Type here...')}
            />
          )}

          {!imageLoaded && (
            <div className='absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg'>
              <Spinner className='h-8 w-8 text-primary-500 animate-spin' />
            </div>
          )}
        </div>
      </div>

      {/* Help text for selected image */}
      {selectedElementId && (
        <div className='absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full max-w-[90%] text-center'>
          {isImageSelected ? (
            <>
              <span className='hidden sm:inline'>
                {t('Drag to move')} • {t('Handles to resize')} •{' '}
                {t('Circle to rotate')} • {t('R for 90°')} •{' '}
                {t('Delete to remove')}
              </span>
              <span className='sm:hidden'>
                {t('Drag to move')} • {t('Resize with handles')}
              </span>
            </>
          ) : (
            <>
              {t('Drag to move')} • {t('Press Delete to remove')}
            </>
          )}
        </div>
      )}
    </div>
  );
}
